/**
 * Offline Storage Service
 * Provides a unified interface for storing data offline using IndexedDB and Capacitor Preferences
 */

import { Preferences } from '@capacitor/preferences';
import logger from '../../utils/logger';

interface StorageMetadata {
  lastSynced?: number;
  version?: number;
  hasLocalChanges?: boolean;
}

interface StoredData<T> {
  data: T;
  metadata: StorageMetadata;
}

class OfflineStorageService {
  private db: IDBDatabase | null = null;
  private dbName = 'marune-offline-db';
  private dbVersion = 1;
  private isInitialized = false;

  /**
   * Initialize the offline storage
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.db = await this.openDB();
      this.isInitialized = true;
      logger.info('Offline storage initialized');
    } catch (error) {
      logger.error('Failed to initialize offline storage:', error);
      // Fall back to Preferences API only
      this.isInitialized = true;
    }
  }

  /**
   * Open IndexedDB database
   */
  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('trainings')) {
          const trainingsStore = db.createObjectStore('trainings', { keyPath: 'id' });
          trainingsStore.createIndex('owner', 'owner', { unique: false });
          trainingsStore.createIndex('date', 'date', { unique: false });
        }

        if (!db.objectStoreNames.contains('profiles')) {
          db.createObjectStore('profiles', { keyPath: 'uid' });
        }

        if (!db.objectStoreNames.contains('goals')) {
          const goalsStore = db.createObjectStore('goals', { keyPath: 'id' });
          goalsStore.createIndex('uid', 'uid', { unique: false });
        }

        if (!db.objectStoreNames.contains('workouts')) {
          const workoutsStore = db.createObjectStore('workouts', { keyPath: 'id' });
          workoutsStore.createIndex('owner', 'owner', { unique: false });
        }

        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  /**
   * Save data to IndexedDB
   */
  async saveToIndexedDB<T>(storeName: string, data: T & { id?: string }, metadata?: StorageMetadata): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const storedData: StoredData<T> = {
      data,
      metadata: {
        lastSynced: Date.now(),
        version: this.dbVersion,
        ...metadata
      }
    };

    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(storedData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save to IndexedDB'));
    });
  }

  /**
   * Get data from IndexedDB
   */
  async getFromIndexedDB<T>(storeName: string, key: string): Promise<StoredData<T> | null> {
    if (!this.db) return null;

    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error('Failed to read from IndexedDB'));
    });
  }

  /**
   * Get all data from a store
   */
  async getAllFromStore<T>(storeName: string): Promise<StoredData<T>[]> {
    if (!this.db) return [];

    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to read all from IndexedDB'));
    });
  }

  /**
   * Query data by index
   */
  async queryByIndex<T>(storeName: string, indexName: string, value: any): Promise<StoredData<T>[]> {
    if (!this.db) return [];

    const transaction = this.db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);

    return new Promise((resolve, reject) => {
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to query IndexedDB'));
    });
  }

  /**
   * Save simple key-value data using Capacitor Preferences
   */
  async savePreference(key: string, value: any): Promise<void> {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(value)
      });
    } catch (error) {
      logger.error('Failed to save preference:', error);
      throw error;
    }
  }

  /**
   * Get simple key-value data using Capacitor Preferences
   */
  async getPreference<T>(key: string): Promise<T | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value ? JSON.parse(value) : null;
    } catch (error) {
      logger.error('Failed to get preference:', error);
      return null;
    }
  }

  /**
   * Remove preference
   */
  async removePreference(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      logger.error('Failed to remove preference:', error);
    }
  }

  /**
   * Clear all preferences
   */
  async clearPreferences(): Promise<void> {
    try {
      await Preferences.clear();
    } catch (error) {
      logger.error('Failed to clear preferences:', error);
    }
  }

  /**
   * Add to sync queue
   */
  async addToSyncQueue(type: string, action: string, data: any): Promise<void> {
    if (!this.db) {
      // Fall back to preferences if DB not available
      const queue = await this.getPreference<any[]>('syncQueue') || [];
      queue.push({ type, action, data, timestamp: Date.now() });
      await this.savePreference('syncQueue', queue);
      return;
    }

    const transaction = this.db.transaction(['syncQueue'], 'readwrite');
    const store = transaction.objectStore('syncQueue');

    await new Promise<void>((resolve, reject) => {
      const request = store.add({
        type,
        action,
        data,
        timestamp: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to add to sync queue'));
    });
  }

  /**
   * Get pending sync items
   */
  async getSyncQueue(): Promise<any[]> {
    if (!this.db) {
      // Fall back to preferences if DB not available
      return await this.getPreference<any[]>('syncQueue') || [];
    }

    const transaction = this.db.transaction(['syncQueue'], 'readonly');
    const store = transaction.objectStore('syncQueue');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get sync queue'));
    });
  }

  /**
   * Remove from sync queue
   */
  async removeFromSyncQueue(id: number): Promise<void> {
    if (!this.db) {
      // Handle preferences fallback
      const queue = await this.getPreference<any[]>('syncQueue') || [];
      const filtered = queue.filter((_, index) => index !== id);
      await this.savePreference('syncQueue', filtered);
      return;
    }

    const transaction = this.db.transaction(['syncQueue'], 'readwrite');
    const store = transaction.objectStore('syncQueue');

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to remove from sync queue'));
    });
  }

  /**
   * Clear entire store
   */
  async clearStore(storeName: string): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear store'));
    });
  }

  /**
   * Get storage info
   */
  async getStorageInfo(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { usage: 0, quota: 0 };
  }
}

// Export singleton instance
const offlineStorageService = new OfflineStorageService();
export default offlineStorageService;